import React, { useContext, useEffect, useState } from "react";
import { supabase } from "../../config/supabase";

const AuthContext = React.createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    supabase.auth.getSession().then(({ data }) => {
      if (cancelled) return;
      setCurrentUser(data.session?.user ?? null);
      setLoading(false);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      setCurrentUser(session?.user ?? null);
    });

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, []);

  // Sign up with email/password. Note: depending on the Supabase project's
  // "Confirm email" setting, the new user may need to verify before a session
  // is established. SignUpScreen surfaces any thrown error.
  const createUserWithEmailPassword = async (email, password) => {
    const { error } = await supabase.auth.signUp({ email, password });
    if (error) throw error;
  };

  const loginWithEmailPassword = async (email, password) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) {
      // Surface a friendly message for the most common failure mode.
      const message =
        error.status === 400
          ? "Email or password incorrect."
          : "An unexpected error occurred. Please try again.";
      throw new Error(message);
    }
  };

  const sendPasswordReset = async (email) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/signin`,
    });
    return error ? "failure" : "success";
  };

  // Google OAuth. Supabase handles the redirect dance; control returns to the
  // app via `redirectTo` (current origin by default). We pass the desired
  // post-login path so the user lands where they expected.
  const loginWithRedirect = async (redirectUri = "/") => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: `${window.location.origin}${redirectUri}`,
      },
    });
    if (error) throw error;
  };

  const logout = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) console.error("Error during sign out:", error);
  };

  const value = {
    currentUser,
    createUserWithEmailPassword,
    loginWithEmailPassword,
    loginWithRedirect,
    logout,
    sendPasswordReset,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
